import React, { ReactNode } from "react";
import { useDroppable, UniqueIdentifier, useDndContext } from "@dnd-kit/core";
import { SortableContext, rectSortingStrategy } from "@dnd-kit/sortable";
import { cn } from "@/lib/utils";
import {
  ContainerHeader,
  ContainerHeaderProps,
} from "@/components/Dashboard/components/Container/Container.Header";
import { SHOW_EMPTY_CONTAINER_PLACEHOLDER } from "@/lib/const";
import {
  ContainerFooter,
  ContainerFooterProps,
} from "@/components/Dashboard/components/Container/Container.Footer";
import { useRoles } from "@/hooks/useRoles";
import { sentry } from "@/services/sentry";

export interface SortableContainerProps
  extends ContainerHeaderProps,
    ContainerFooterProps {
  id: UniqueIdentifier;
  items: UniqueIdentifier[];
  children: ReactNode;
  className?: string;
}

export const SortableContainer: React.FC<SortableContainerProps> = ({
  id,
  items,
  children,
  className,
  ...props
}) => {
  const { isViewOnly } = useRoles();
  const { setNodeRef } = useDroppable({
    id,
    disabled: isViewOnly,
    data: {
      type: "container",
      accepts: ["item"],
      children: items,
    },
  });

  const { active } = useDndContext();

  // Only highlight when an item is being dragged over this container
  const isActiveItem = active?.data.current?.containerId === id;

  return (
    <SortableContext
      disabled={isViewOnly}
      id={id.toString()}
      items={items}
      strategy={rectSortingStrategy}
    >
      <div
        ref={setNodeRef}
        className={cn(
          // Base styles
          "flex flex-col overflow-hidden rounded-lg p-1 pt-1 transition-colors duration-200",
          // Default state
          "border-gray-500",
          // Highlight when item is dragged over container
          isActiveItem ? "bg-opacity-75" : "border-none",
          // Custom classes
          className
        )}
        data-container-id={id}
      >
        <div className="text-white">
          <ContainerHeader {...props} />
        </div>
        {/*
          The p-2 (8px) padding is load-bearing, not cosmetic. The selected-card
          highlight in Card.tsx is an OUTER box-shadow that paints 5px beyond the
          card's border box — 7.5px at the maximum card zoom of 1.5, rounded up to
          the next Tailwind step. An outer box-shadow is ink overflow: it never
          becomes scrollable area, so without this padding the scrollport clips the
          ring off the first-row cards (and shaves the left/right-most ones). Keep
          this >= the ring width in Card.tsx times the maximum card zoom in
          CARD_ZOOM_LEVELS.
        */}
        <div className="flex-grow overflow-auto p-2">
          <div
            className={cn(
              // content-start: without it, this row's own min-h-24 (96px)
              // floor gets redistributed by align-content's stretch default
              // into whichever single line exists before enough cards wrap
              // to a second row — and align-items' stretch default then
              // inflates every card in that line to match, regardless of
              // card count. content-start stops align-content from pushing
              // that floor-induced space into line sizing, so a line's
              // cross size is driven by its own content instead. This
              // deliberately keeps align-items: stretch's per-line
              // equalization (cards sharing a line still match the
              // tallest card in that line) — items-start would remove
              // that too, giving mixed-height rows a ragged bottom edge.
              // See WEBV3-199.
              "flex min-h-24 w-full flex-row flex-wrap content-start gap-0.5 pb-2",
              items.length === 0 &&
                SHOW_EMPTY_CONTAINER_PLACEHOLDER &&
                "flex min-h-[50px] flex-grow flex-row flex-wrap content-start rounded border border-dashed border-gray-300 p-2"
            )}
            style={{
              minHeight: items.length === 0 ? "50px" : undefined,
            }}
          >
            {children}
          </div>
        </div>
        <ContainerFooter {...props} />
      </div>
    </SortableContext>
  );
};

export const ProfiledSortableContainer = sentry.withProfiler(
  SortableContainer,
  { name: "Sortable Container" }
);
